import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { normalizeFilter } from '@arthelper/shared/image-library';
import { isNonArtworkContext } from '@arthelper/shared/image-context';
import type { UploadFailureStep } from '@arthelper/shared/upload-failure';

import { LIBRARY_IMAGES_QUERY } from '../graphql/queries';
import { RETRY_IMAGE_MUTATION } from '../graphql/mutations';
import { listFailedUploads, removeFailedUpload } from '../utils/failed-uploads';
import type { ImageItem, UploadingImage } from '~/types';
import type { UploadSource } from '@arthelper/shared/upload-origin';

/** One upload the member has to be told about (#4159): it never became artwork. */
export interface FailedUpload {
  /** The placeholder's client id, or the image id for a failed row read from the server. */
  id: string;
  fileName: string;
  preview?: string;
  step: UploadFailureStep;
  /** Set when an image row exists; retry then re-runs processing instead of re-sending. */
  serverId?: string;
  /** A pre-save failure keeps its File so Retry can send it again. */
  canRetry: boolean;
}

const FAILED = ['failed', 'error'];
const FAILED_LIMIT = 100;

type SetUploading = React.Dispatch<React.SetStateAction<Record<string, UploadingImage>>>;

function fromPlaceholder(img: UploadingImage): FailedUpload {
  return {
    id: img.id,
    fileName: img.fileMeta?.fileName || 'image',
    preview: img.preview || undefined,
    step: img.failedStep ?? 'processing',
    serverId: img.serverId,
    canRetry: !!img.serverId || !!img.file,
  };
}

function fromRow(row: ImageItem): FailedUpload {
  return {
    id: String(row.id),
    fileName: row.fileName || row.title || 'image',
    preview: row.thumbnailUrl || undefined,
    step: 'processing',
    serverId: String(row.id),
    canRetry: true,
  };
}

/** Puts the files a previous page load failed to save back on screen as failed placeholders. */
function useRestoreStored(userId: string | null, setUploadingImages: SetUploading) {
  useEffect(() => {
    if (!userId) {
      return;
    }

    let cancelled = false;
    listFailedUploads(userId).then((stored) => {
      if (cancelled || stored.length === 0) {
        return;
      }

      setUploadingImages((prev) =>
        stored.reduce(
          (memo, entry) => {
            if (memo[entry.id]) {
              return memo;
            }

            const file = new File([entry.file], entry.fileName, { type: entry.fileType });
            memo[entry.id] = {
              id: entry.id,
              status: 'error',
              uploadProgress: 0,
              preview: URL.createObjectURL(file),
              fileMeta: { fileName: entry.fileName },
              collectionId: entry.collectionId ?? undefined,
              file,
              uploadError: entry.message,
              failedStep: entry.step,
              uploadSource: entry.uploadSource,
            };
            return memo;
          },
          { ...prev }
        )
      );
    });

    return () => {
      cancelled = true;
    };
  }, [userId, setUploadingImages]);
}

/**
 * Every upload that did not become artwork, from three places: this session's failed
 * placeholders, files a previous load failed to save (IndexedDB), and image rows the
 * processor marked failed (the server). Retry and Remove act on whichever it is.
 */
export function useFailedUploads({
  userId,
  uploadingImages,
  setUploadingImages,
  uploadImage,
  deleteImage,
  onRetried,
}: {
  userId: string | null;
  uploadingImages: Record<string, UploadingImage>;
  setUploadingImages: SetUploading;
  uploadImage: (args: {
    file: File;
    collectionId: string | null;
    source: UploadSource;
    retryOf?: string;
  }) => Promise<unknown>;
  deleteImage: (id: string) => Promise<void>;
  /** A server row went back to the processor; the caller starts polling it. */
  onRetried: (item: FailedUpload) => void;
}) {
  useRestoreStored(userId, setUploadingImages);

  const { data, refetch } = useQuery(LIBRARY_IMAGES_QUERY, {
    variables: { first: FAILED_LIMIT, filter: normalizeFilter({ statuses: FAILED }) },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [retryImageMutation] = useMutation(RETRY_IMAGE_MUTATION);
  // Pre-save retry reads the File, drops the placeholder, then uploads again.
  // A second call in that window (Retry All twice, or Retry All plus the row)
  // would presign, PUT, and createImage a second copy. Server retries are
  // already one-shot via the conditional status update.
  const retrying = useRef(new Set<string>());
  // Removed by the member, as opposed to retried: a MediaDrop chip still holding the id
  // has to send its file again rather than treat the upload as handled.
  const dismissed = useRef(new Set<string>());
  const wasDismissed = useCallback((id: string) => dismissed.current.has(id), []);

  const failedUploads: FailedUpload[] = useMemo(() => {
    const placeholders = Object.values(uploadingImages);
    const local = placeholders.filter((img) => img.status === 'error').map(fromPlaceholder);
    // A row with any placeholder is either shown above or back in flight after a retry.
    const covered = new Set(placeholders.map((img) => img.serverId).filter(Boolean));
    const rows: ImageItem[] = (data?.myImages?.edges ?? []).map((edge: any) => edge.node);
    const server = rows.filter((row) => !covered.has(String(row.id)) && !isNonArtworkContext(row.context)).map(fromRow);

    return [...local, ...server];
  }, [uploadingImages, data]);

  // Off screen only. The stored record stays until a retry lands, so a retry that stalls or
  // a tab closed mid-retry still leaves the failure listed on the next load.
  const dropPlaceholder = useCallback(
    (id: string) => {
      setUploadingImages((prev) => {
        if (!prev[id]) {
          return prev;
        }

        const next = { ...prev };
        if (next[id].preview) {
          URL.revokeObjectURL(next[id].preview);
        }
        delete next[id];
        return next;
      });
    },
    [setUploadingImages]
  );

  const forget = useCallback(
    (id: string) => {
      dropPlaceholder(id);
      removeFailedUpload(id);
    },
    [dropPlaceholder]
  );

  const retryUpload = useCallback(
    async (item: FailedUpload) => {
      if (retrying.current.has(item.id)) {
        return;
      }
      retrying.current.add(item.id);

      try {
        const placeholder = uploadingImages[item.id];
        if (!item.serverId && placeholder?.file) {
          dropPlaceholder(item.id);
          // uploadImage reports its own failure (placeholder, store, event, toast).
          await uploadImage({
            file: placeholder.file,
            collectionId: placeholder.collectionId ?? null,
            // Every placeholder records its source; 'unknown' only satisfies the type.
            source: placeholder.uploadSource ?? 'unknown',
            retryOf: item.id,
          }).catch(() => undefined);
          return;
        }

        if (!item.serverId) {
          return;
        }

        await retryImageMutation({ variables: { id: item.serverId } });
        onRetried(item);
        await refetch();
      } finally {
        retrying.current.delete(item.id);
      }
    },
    [uploadingImages, dropPlaceholder, uploadImage, retryImageMutation, onRetried, refetch]
  );

  const dismissFailedUpload = useCallback(
    async (item: FailedUpload) => {
      // A retry in flight may already have moved the row back to the processor.
      if (retrying.current.has(item.id)) {
        return;
      }

      dismissed.current.add(item.id);
      forget(item.id);
      if (!item.serverId) {
        return;
      }

      await deleteImage(item.serverId);
      await refetch();
    },
    [forget, deleteImage, refetch]
  );

  return { failedUploads, retryUpload, dismissFailedUpload, wasDismissed, refetchFailed: refetch };
}
